/**
 * Common Constants
 * 
 * Centralized constants to avoid magic numbers scattered across codebase
 */

// ========================
// PAGINATION DEFAULTS
// ========================
export const PAGINATION = {
  DEFAULT_LIMIT: 20,
  MAX_LIMIT: 100,
  MIN_LIMIT: 1,
  
  // Domain-specific defaults
  PLANS_LIMIT: 50,
  STAGES_LIMIT: 100,
  MESSAGES_LIMIT: 50,
  POSTS_LIMIT: 20,
  COMMENTS_LIMIT: 50,
  LEADERBOARD_LIMIT: 50,
  SESSIONS_LIMIT: 30,
  NOTIFICATIONS_LIMIT: 50,
  SEARCH_RESULTS_LIMIT: 20
} as const

// ========================
// CACHE TTL (milliseconds)
// ========================
export const CACHE_TTL = {
  // Generic TTLs
  SHORT: 30 * 1000,           // 30 seconds
  MEDIUM: 2 * 60 * 1000,      // 2 minutes
  STANDARD: 5 * 60 * 1000,    // 5 minutes
  LONG: 15 * 60 * 1000,       // 15 minutes
  HOUR: 60 * 60 * 1000,       // 1 hour
  DAY: 24 * 60 * 60 * 1000,   // 24 hours

  // Domain-specific (milliseconds)
  USER_PROFILE: 5 * 60 * 1000,      // 5 minutes
  PLAN_METRICS: 5 * 60 * 1000,      // 5 minutes
  USER_BEHAVIOR: 2 * 60 * 1000,     // 2 minutes
  SESSIONS: 30 * 1000,              // 30 seconds
  LOGIN_HISTORY: 45 * 1000,         // 45 seconds
  POI_GEOHASH: 10 * 60 * 1000,      // 10 minutes
  SEASON_CONFIG: 15 * 60 * 1000,    // 15 minutes
  WEATHER: 10 * 60 * 1000,          // 10 minutes
  LOCATION_NAME: 24 * 60 * 60 * 1000, // 24 hours
  
  // Domain-specific (seconds for Redis)
  SEARCH_CACHE_SEC: 300,            // 5 minutes
  FEED_CACHE_SEC: 600,              // 10 minutes
  ANALYTICS_CACHE_SEC: 3600,        // 1 hour
  CURRENCY_CACHE_SEC: 3600,         // 1 hour
  STATS_CACHE_SEC: 300,             // 5 minutes
  USER_CACHE_SEC: 3600,             // 1 hour
  LOCATION_CACHE_SEC: 30            // 30 seconds (live tracking)
} as const

// ========================
// RATE LIMITS
// ========================
export const RATE_LIMITS = {
  AI_REQUESTS_PER_MINUTE: 10,
  AI_REQUESTS_PER_DAY: 100,
  LOGIN_ATTEMPTS_PER_HOUR: 5,
  API_REQUESTS_PER_MINUTE: 100,
  WEBHOOK_RETRIES: 3
} as const

// ========================
// FIRESTORE LIMITS
// ========================
export const FIRESTORE_LIMITS = {
  BATCH_SIZE: 500,
  MAX_IN_QUERY: 30,       // .where('x', 'in', [...]) max 30 items
  MAX_ARRAY_CONTAINS: 10, // .where('x', 'array-contains-any', [...]) max 10
  DOC_SIZE_BYTES: 1_048_576 // 1MB max document size
} as const

// ========================
// COLLECTION NAMES
// ========================
export const COLLECTIONS = {
  USERS: 'users',
  PLANS: 'plans',
  STAGES: 'stages',
  SHARE_LINKS: 'shareLinks',
  BUDGETS: 'budgets',
  EXPENSES: 'expenses',
  PLAN_EXPENSES: 'plan_expenses',
  NOTIFICATIONS: 'notifications',
  CLANS: 'clans',
  SEASONS: 'seasons',
  BADGES: 'badges',
  QUESTS: 'quests',
  CHALLENGES: 'challenges',
  LIVE_SESSIONS: 'live-sessions',
  AI_JOBS: 'ai_jobs',
  POI_CACHE: 'poi_cache',
  BILLING_PLANS: 'billing_plans',
  USER_EXPORTS: 'userExports'
} as const

// ========================
// REDIS KEY PREFIXES
// ========================
export const REDIS_KEYS = {
  YDOC: 'ydoc',
  PRESENCE: 'presence',
  PLAN_ACCESS: 'plan:access',
  RATE_LIMIT: 'ratelimit',
  SESSION: 'session',
  CACHE: 'cache'
} as const

// ========================
// USER TIERS
// ========================
export const USER_TIERS = {
  FREE: 'free',
  PREMIUM: 'premium',
  PRO: 'pro'
} as const

// ========================
// MAX PARTICIPANTS (Live sessions)
// ========================
export const MAX_PARTICIPANTS = {
  FREE: 5,
  PREMIUM: 20,
  PRO: 50
} as const

// ========================
// RETRY SETTINGS
// ========================
export const RETRY_CONFIG = {
  MAX_RETRIES: 3,
  BASE_DELAY_MS: 100,
  MAX_DELAY_MS: 5000
} as const
